import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { SreenCoverLoading, useRadioBoxGroup, usePopupPage, Input } from "src/components";
import { IoSearchOutline } from "react-icons/io5";
import {
  AnnouncementAgreePopupContents,
  ClubFindPopupContents,
} from "src/components/PopupPageContents";
import SignModalContent from "src/components/YearBooking/SignModalContent";

import {
  useAlert,
  useGroupLocation,
  useUserStepStatusEvent,
  useRefetchEventBookings,
} from "src/hooks";
import AlertModal from "src/components/YearBooking/components/common/AlertModal";
import { createTeeshotAxiosInstance } from "src/http";
import { recoilStates } from "src/recoil/recoilStates";
import { ClubType, SelectedClubType } from "src/types";
import STATIC_DATA from "src/staticData";
import styled from "styled-components";

// Styled Input for disabled state
const StyledInput = styled.input<{ $disabled?: boolean }>`
  width: 100%;
  height: 44px;
  padding: 13px 10px;
  border-radius: 6px;
  border: 1px solid ${(p) => (p.$disabled ? "#007bff" : "#d1d5d8")};
  background: ${(p) => (p.$disabled ? "#f5f5f5" : "#fdfdfd")};
  color: ${(p) => (p.$disabled ? "#999999" : "#1a1a1a")};
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "text")};
  outline: none;

  &:focus {
    border-color: ${(p) => (p.$disabled ? "#d1d5d8" : "#007bff")};
  }

  &::placeholder {
    color: #999999;
  }
`;

// Event Type Options
const eventTypeOptions = [
  { id: "1", name: "월례회" },
  { id: "2", name: "창립 기념 행사" },
  { id: "3", name: "VIP 고객 행사" },
  { id: "4", name: "자선 행사" },
  { id: "5", name: "브랜드 행사" },
  { id: "6", name: "티칭 프로 행사" },
  { id: "7", name: "투어 프로 행사" },
  { id: "8", name: "기타 행사" },
];

// Time Options (same as YearBookingRegister)
const times = [
  { id: 1, name: "06시대", str: "0600" },
  { id: 2, name: "07시대", str: "0700" },
  { id: 3, name: "08시대", str: "0800" },
  { id: 4, name: "09시대", str: "0900" },
  { id: 5, name: "10시대", str: "1000" },
  { id: 6, name: "11시대", str: "1100" },
  { id: 7, name: "12시대", str: "1200" },
  { id: 8, name: "13시대", str: "1300" },
  { id: 9, name: "14시대", str: "1400" },
  { id: 10, name: "15시대", str: "1500" },
  { id: 11, name: "16시대", str: "1600" },
  { id: 12, name: "17시대", str: "1700" },
  { id: 13, name: "18시대", str: "1800" },
  { id: 14, name: "19시대", str: "1900" },
  { id: 15, name: "20시대", str: "2000" },
  { id: 16, name: "21시대", str: "2100" },
];

// Event Type Modal Component
const EventTypeModal = ({
  isOpen,
  onClose,
  onSelect,
  selectedValue,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (selectedType: string) => void;
  selectedValue?: string;
}) => {
  const [selectedTypeId, setSelectedTypeId] = useState(selectedValue || "");

  const handleTypeSelect = (typeId: string) => {
    setSelectedTypeId(typeId);
  };

  const handleConfirm = () => {
    onSelect(selectedTypeId);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>행사구분</ModalTitle>
          </ModalHeader>

          <OptionsContainer>
            {eventTypeOptions.map((option) => (
              <OptionButton
                key={option.id}
                isSelected={selectedTypeId === option.id}
                onClick={() => handleTypeSelect(option.id)}
              >
                <OptionContent>
                  <CheckIcon isActive={selectedTypeId === option.id}>
                    {selectedTypeId === option.id && (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10 3L4.5 8.5L2 6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </CheckIcon>
                  <OptionText>{option.name}</OptionText>
                </OptionContent>
              </OptionButton>
            ))}
          </OptionsContainer>
        </ModalContent>

        <ModalButtons>
          <CancelButton onClick={onClose}>뒤로</CancelButton>
          <SelectButton onClick={handleConfirm}>확인</SelectButton>
        </ModalButtons>
      </ModalContainer>
    </ModalOverlay>
  );
};

// Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(1, 1, 1, 0.86);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 9999;
  animation: fade-in 0.2s;

  @keyframes fade-in {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div`
  max-width: 800px;
  width: calc(100% - 32px);
  height: auto;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  border-radius: 6px;
  background: #fdfdfd;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 0;
`;

const ModalContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 24px;
  flex: 1;
`;

const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModalTitle = styled.h2`
  align-self: stretch;
  color: #1a1a1a;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  margin: 0;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const OptionButton = styled.button<{ isSelected: boolean }>`
  display: flex;
  width: 100%;
  height: 40px;
  justify-content: flex-start;
  align-items: center;
  border-radius: 6px;
  background: #f8f9fa;
  border: 1px solid ${(props) => (props.isSelected ? "#007BFF" : "#E5E7EB")};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f3f4;
  }
`;

const OptionContent = styled.div`
  display: flex;
  padding: 8px 10px;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const CheckIcon = styled.div<{ isActive: boolean }>`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  aspect-ratio: 1/1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${(props) => (props.isActive ? "#007BFF" : "#D1D5D8")};
  color: white;
`;

const OptionText = styled.span`
  color: #1a1a1a;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  margin-left: 10px;
`;

const ModalButtons = styled.div`
  display: flex;
  height: 60px;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  width: 100%;
`;

const CancelButton = styled.button`
  display: flex;
  flex: 1;
  height: 60px;
  padding: 16px 46px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 0 0 0 6px;
  background: #9ca3af;
  border: none;
  cursor: pointer;
  color: #ffffff;
  text-align: center;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;

  &:hover {
    opacity: 0.9;
  }
`;

const SelectButton = styled.button`
  display: flex;
  flex: 1;
  height: 60px;
  padding: 16px 46px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 0 0 6px 0;
  background: #004b49;
  border: none;
  cursor: pointer;
  color: #ffffff;
  text-align: center;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;

  &:hover {
    opacity: 0.9;
  }
`;

export default function EventBookingRegister() {
  const navigate = useNavigate();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const groupLocations = useGroupLocation();
  const [user] = useRecoilState(recoilStates.user);
  const alert = useAlert();

  // AlertModal 상태 관리
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 5;

  const userStep = useUserStepStatusEvent();
  const refetchEventBooking = useRefetchEventBookings();

  // MainContent ref for scroll control
  const mainContentRef = useRef<HTMLDivElement>(null);

  // Step 변경 시 스크롤을 맨 위로 이동
  useEffect(() => {
    const scrollToTop = () => {
      // MainContent 컨테이너 스크롤
      if (mainContentRef.current) {
        mainContentRef.current.scrollTop = 0;
        mainContentRef.current.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        });
      }

      // 전체 페이지 스크롤도 함께 처리
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    };

    // 즉시 실행
    scrollToTop();

    // 컴포넌트 렌더링 완료 후에도 스크롤 실행
    setTimeout(scrollToTop, 100);
    setTimeout(scrollToTop, 300);
  }, [currentStep]);

  // Step navigation functions
  const goToNextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // 유효성 검사 함수
  const checkSubmitData = (data: any) => {
    if (!selectedClubs || selectedClubs.length < 2) {
      return { isOk: false, message: "희망 골프장을 2개 이상 선택해주세요." };
    }
    if (!proposerNameValue.trim()) {
      return { isOk: false, message: "신청인 이름을 입력해주세요." };
    }
    if (!contactValue.trim()) {
      return { isOk: false, message: "신청인 연락처를 입력해주세요." };
    }
    if (!eventNameValue.trim()) {
      return { isOk: false, message: "이벤트명을 입력해주세요." };
    }
    if (!teamCountValue.trim()) {
      return { isOk: false, message: "신청 팀수를 입력해주세요." };
    }
    if (!startDate) {
      return { isOk: false, message: "희망 날짜를 선택해주세요." };
    }
    if (!selectedTime) {
      return { isOk: false, message: "희망 시간을 선택해주세요." };
    }
    return { isOk: true };
  };

  // Submit handler - opens signature modal
  const handleSubmit = () => {
    const check = checkSubmitData({});
    if (!check.isOk) {
      alert({
        content: check.message,
        onClose: () => alert.resume(),
      });
      return;
    }
    setIsSignModalOpened(true);
  };

  // Actual submit after signature
  const handleFinalSubmit = async () => {
    if (!signBase64) {
      alert({ content: "서명을 완료해주세요." });
      return;
    }

    setIsSubmitLoading(true);
    try {
      const submitData = {
        registererName: proposerNameValue,
        contact: contactValue,
        eventName: eventNameValue,
        clubs: selectedClubs?.map((c) => {
          return { id: c.id, preference: c.preference };
        }),
        scheduleTypeId: 1,
        eventTypeId: 1,
        startDate: startDate,
        dateTimeValues: [{ date: startDate, time: "0700" }],
        playTypeIds: activePlayTypeIds,
        customerSignBase64: signBase64,
      };

      const axios = createTeeshotAxiosInstance();
      const res = await axios.post("/evtBooking/booking", {
        submitData: { ...submitData, customerSignBase64: signBase64 },
      });

      if (res.data.success === false) {
        throw new Error();
      }

      // 서명 모달을 닫고 성공 모달을 표시
      setIsSignModalOpened(false);
      setSignBase64(null);

      // 성공 시 월단체 신청완료 모달 표시
      alert({
        title: "월단체 신청완료",
        content: (
          <div className="d-flex fd-c gap-2">
            <p>월단체 부킹신청이 완료되었습니다.</p>
            <p>
              * 신청하신 조건을 골프장에서 검토한 후에 "승낙 / 변경조건 / 거절"을 앱내 통지드립니다.
            </p>
          </div>
        ),
        onClose: () => {
          alert.resume();
        },
      });
      await alert.pause();
      await refetchEventBooking();
      navigate("/event-booking");
    } catch (error: any) {
      console.error("Submit error:", error);
      let errorMessage = "신청 중 오류가 발생했습니다.";
      if (error?.code === "ECONNABORTED" || String(error?.message || "").includes("timeout")) {
        errorMessage = "서버 응답이 지연되고 있습니다. 잠시 후 다시 시도해주세요.";
      } else if (error?.response?.status >= 500) {
        errorMessage = "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.";
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      alert({ content: errorMessage });
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const getProgressWidth = () => {
    return (currentStep / totalSteps) * 100;
  };

  useEffect(() => {
    if (userStep === "waitingProposal") {
      (async () => {
        alert({ content: "이미 부킹을 신청했습니다.", onClose: () => alert.resume() });
        await alert.pause();
        navigate("/event-booking");
      })();
    }
  }, [userStep, alert, navigate]);

  const [selectedClubs, setSelectedClubs] = useState<SelectedClubType[]>();
  const [announcementShowingClub, setAnnouncementShowingClub] = useState<ClubType>();
  const [isSignModalOpened, setIsSignModalOpened] = useState(false);
  const [signBase64, setSignBase64] = useState<string | null>(null);

  // Form data
  const [proposerNameValue, setProposerNameValue] = useState("");
  const [contactValue, setContactValue] = useState("");
  const [eventNameValue, setEventNameValue] = useState("");
  const [teamCountValue, setTeamCountValue] = useState("");
  const [startDate, _setStartDate] = useState("");
  const [eventTypeValue, setEventTypeValue] = useState("");
  const [isEventTypeModalOpen, setIsEventTypeModalOpen] = useState(false);
  const [scheduleType, setScheduleType] = useState("");
  const [_roundStartTime, _setRoundStartTime] = useState("");
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState("");

  // user 정보가 있을 때 초기값으로만 설정 (최초 한 번만)
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    // 이미 초기화했으면 더 이상 자동 설정하지 않음
    if (hasInitialized) return;

    // user 정보를 trim 처리하고 유효한 값만 확인
    const trimmedRealname = user?.realname?.trim();
    const trimmedPhone = user?.phone?.trim();

    // user 정보가 있을 때는 초기값으로 설정
    if (trimmedRealname || trimmedPhone) {
      setProposerNameValue(trimmedRealname || "");
      setContactValue(trimmedPhone || "");
    }

    // user 정보가 있든 없든 초기화 완료로 표시 (사용자가 입력할 수 있도록)
    setHasInitialized(true);
  }, [user, hasInitialized]);

  // 사용자 정보가 있는지 확인 (user.realname이 있으면 계속 수정 불가능)
  const hasUserInfo = Boolean(user?.realname?.trim());

  const {
    PopupComponent: AnnouncePopupComp,
    openPopup: openAnnouncePopup,
    closePopup: closeAnnounce,
  } = usePopupPage("announcement");

  const {
    PopupComponent: ClubFindPopupComp,
    openPopup: openClubSearchPopup,
    closePopup: closeClubSearchPopup,
  } = usePopupPage("clubSearch");

  // Step 4: Round Options
  const { RadioBoxGroup: PlayTypeBoxGroup, activeIds: activePlayTypeIds } = useRadioBoxGroup({
    boxType: "round",
    items: STATIC_DATA.GAME_TYPE_DATA,
  });

  const { RadioBoxGroup: TeeTypeBoxGroup, activeIds: activeTeeTypeIds } = useRadioBoxGroup({
    boxType: "round",
    items: STATIC_DATA.TEEBOX_TYPE_DATA,
  });

  const { RadioBoxGroup: PricePerPersonBoxGroup, activeIds: activePricePerPersonIds } =
    useRadioBoxGroup({
      boxType: "round",
      items: [
        { id: 1, name: "1만원" },
        { id: 2, name: "2만원" },
        { id: 3, name: "3만원" },
        { id: 4, name: "4만원" },
        { id: 5, name: "5만원+" },
      ],
    });

  const { RadioBoxGroup: EtcRequirementsBoxGroup, activeIds: activeEtcRequirementsIds } =
    useRadioBoxGroup({
      multipleValue: true,
      boxType: "round",
      items: STATIC_DATA.ETC_REQ_DATA,
    });

  const { RadioBoxGroup: AwardsBoxGroup, activeIds: activeAwardsIds } = useRadioBoxGroup({
    multipleValue: true,
    boxType: "round",
    multiLineColumnCount: 3,
    items: STATIC_DATA.AWARDS_DATA,
  });

  const { RadioBoxGroup: MealActiveBoxGroup, activeIds: mealActiveIds } = useRadioBoxGroup({
    boxType: "round",
    items: [
      { id: 1, name: "유" },
      { id: 2, name: "무" },
    ],
  });

  const { RadioBoxGroup: MealTypeBoxGroup, activeIds: mealTypeActiveIds } = useRadioBoxGroup({
    boxType: "round",
    items: STATIC_DATA.MEAL_TYPE_DATA,
  });

  const { RadioBoxGroup: RoomActiveBoxGroup, activeIds: roomActiveIds } = useRadioBoxGroup({
    boxType: "round",
    items: [
      { id: 1, name: "라운드 전" },
      { id: 2, name: "라운드 후" },
      { id: 3, name: "이용하지 않아요" },
    ],
  });

  const { RadioBoxGroup: ShopActiveBoxGroup, activeIds: shopActiveIds } = useRadioBoxGroup({
    boxType: "round",
    items: [
      { id: 1, name: "유" },
      { id: 2, name: "무" },
    ],
  });

  const { value: shopItemValue, onChange: handleShopItemValueChange } = Input.hooks.useInput("");

  const { RadioBoxGroup: PayTypeBoxGroup, activeIds: activePayTypeIds } = useRadioBoxGroup({
    boxType: "round",
    items: STATIC_DATA.PAY_TYPE_DATA,
  });

  const { value: additionalCommentValue, onChange: onAdditionalCommentChange } =
    Input.hooks.useInput("");

  const [currentClubPreferenceNumber, setCurrentClubPreferenceNumber] = useState(1);

  // Render current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="flex-1 d-flex fd-c gap-2">
            <div className="d-flex fd-c gap-2">
              <h2 className="bold" style={{ fontSize: "18px", marginBottom: "8px" }}>
                희망 골프장 3개를 선택해주세요.
              </h2>
              <div className="d-flex ai-c gap-1">
                <span style={{ color: "#FF6B35", fontSize: "14px" }}>*</span>
                <span style={{ fontSize: "14px", color: "#000" }}>골프장 선택</span>
                <span style={{ fontSize: "14px", color: "#666" }}> (2개 이상)</span>
              </div>
            </div>

            <div className="d-flex fd-c gap-3">
              <div
                className="d-flex ai-c cursor-p"
                onClick={() => {
                  setCurrentClubPreferenceNumber(1);
                  openClubSearchPopup();
                }}
                style={{
                  padding: "12px",
                  border: selectedClubs?.find((club) => club.preference === 1)?.name
                    ? "1px solid #007BFF"
                    : "1px solid #E0E0E0",
                  borderRadius: "8px",
                  backgroundColor: "#FAFAFA",
                  minHeight: "56px",
                }}
              >
                <span
                  style={{
                    color: selectedClubs?.find((club) => club.preference === 1)?.name
                      ? "#000"
                      : "#999",
                    fontSize: "16px",
                    flex: 1,
                  }}
                >
                  {selectedClubs?.find((club) => club.preference === 1)?.name ||
                    "골프장을 선택하세요."}
                </span>
                <IoSearchOutline size={25} style={{ color: "#79818a" }} />
              </div>

              <div
                className="d-flex ai-c cursor-p"
                onClick={() => {
                  if (!(selectedClubs && selectedClubs.some((club) => club.preference === 1))) {
                    setAlertMessage("1지망 골프장을 먼저 선택하세요.");
                    setShowAlertModal(true);
                    return;
                  }
                  setCurrentClubPreferenceNumber(2);
                  openClubSearchPopup();
                }}
                style={{
                  padding: "12px",
                  border: selectedClubs?.find((club) => club.preference === 2)?.name
                    ? "1px solid #007BFF"
                    : "1px solid #E0E0E0",
                  borderRadius: "8px",
                  backgroundColor: "#FAFAFA",
                  minHeight: "56px",
                }}
              >
                <span
                  style={{
                    color: selectedClubs?.find((club) => club.preference === 2)?.name
                      ? "#000"
                      : "#999",
                    fontSize: "16px",
                    flex: 1,
                  }}
                >
                  {selectedClubs?.find((club) => club.preference === 2)?.name ||
                    "골프장을 선택하세요."}
                </span>
                <IoSearchOutline size={25} style={{ color: "#79818a" }} />
              </div>

              <div
                className="d-flex ai-c cursor-p"
                onClick={() => {
                  if (!(selectedClubs && selectedClubs.some((club) => club.preference === 2))) {
                    setAlertMessage("1, 2지망 골프장을 먼저 선택하세요.");
                    setShowAlertModal(true);
                    return;
                  }
                  setCurrentClubPreferenceNumber(3);
                  openClubSearchPopup();
                }}
                style={{
                  padding: "12px",
                  border: selectedClubs?.find((club) => club.preference === 3)?.name
                    ? "1px solid #007BFF"
                    : "1px solid #E0E0E0",
                  borderRadius: "8px",
                  backgroundColor: "#FAFAFA",
                  minHeight: "56px",
                }}
              >
                <span
                  style={{
                    color: selectedClubs?.find((club) => club.preference === 3)?.name
                      ? "#000"
                      : "#999",
                    fontSize: "16px",
                    flex: 1,
                  }}
                >
                  {selectedClubs?.find((club) => club.preference === 3)?.name ||
                    "골프장을 선택하세요."}
                </span>
                <IoSearchOutline size={25} style={{ color: "#79818a" }} />
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="flex-1 d-flex fd-c gap-4">
            <div className="d-flex fd-c gap-2">
              <h2 className="bold" style={{ fontSize: "18px", marginBottom: "8px" }}>
                신청 정보를 입력해주세요.
              </h2>
            </div>

            <div className="d-flex fd-c gap-4">
              <div className="d-flex fd-c gap-1">
                <div className="d-flex ai-c gap-1">
                  <span style={{ color: "#FF6B35", fontSize: "14px" }}>*</span>
                  <span style={{ fontSize: "14px", color: "#000", fontWeight: "500" }}>
                    신청인 이름
                  </span>
                </div>
                <StyledInput
                  placeholder="신청인 이름을 입력해주세요."
                  value={proposerNameValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setProposerNameValue(e.target.value)
                  }
                  disabled={hasUserInfo}
                  $disabled={hasUserInfo}
                />
              </div>

              <div className="d-flex fd-c gap-1">
                <div className="d-flex ai-c gap-1">
                  <span style={{ color: "#FF6B35", fontSize: "14px" }}>*</span>
                  <span style={{ fontSize: "14px", color: "#000", fontWeight: "500" }}>
                    신청인 연락처
                  </span>
                </div>
                <StyledInput
                  placeholder="연락처를 입력해주세요. (예시: 01012345678)"
                  value={contactValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setContactValue(e.target.value)
                  }
                  disabled={hasUserInfo}
                  $disabled={hasUserInfo}
                />
              </div>

              <div className="d-flex fd-c gap-1">
                <div className="d-flex ai-c gap-1">
                  <span style={{ color: "#FF6B35", fontSize: "14px" }}>*</span>
                  <span style={{ fontSize: "14px", color: "#000", fontWeight: "500" }}>
                    행사 구분
                  </span>
                </div>
                <div
                  onClick={() => setIsEventTypeModalOpen(true)}
                  style={{
                    width: "100%",
                    height: "44px",
                    padding: "13px 10px",
                    border: eventTypeValue ? "1px solid #007BFF" : "1px solid #d1d5d8",
                    borderRadius: "6px",
                    backgroundColor: "#fdfdfd",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    fontFamily: "Pretendard, -apple-system, Roboto, Helvetica, sans-serif",
                    fontSize: "14px",
                    fontWeight: "400",
                    lineHeight: "18px",
                    color: eventTypeValue ? "#1a1a1a" : "#999999",
                  }}
                >
                  <span>
                    {eventTypeValue
                      ? eventTypeOptions.find((option) => option.id === eventTypeValue)?.name ||
                        "행사 성격을 선택해주세요."
                      : "행사 성격을 선택해주세요."}
                  </span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    style={{ color: "#6b7280" }}
                  >
                    <path
                      d="M6 9L12 15L18 9"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>

              <div className="d-flex fd-c gap-1">
                <div className="d-flex ai-c gap-1">
                  <span style={{ color: "#FF6B35", fontSize: "14px" }}>*</span>
                  <span style={{ fontSize: "14px", color: "#000", fontWeight: "500" }}>행사명</span>
                </div>
                <StyledInput
                  placeholder="행사명을 입력해주세요. (예시: 경기고등학교 45기 동창회)"
                  value={eventNameValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setEventNameValue(e.target.value)
                  }
                  $disabled={false}
                  style={{
                    border: eventNameValue ? "1px solid #007BFF" : "1px solid #d1d5d8",
                  }}
                />
              </div>

              <div className="d-flex fd-c gap-1">
                <div className="d-flex ai-c gap-1">
                  <span style={{ color: "#FF6B35", fontSize: "14px" }}>*</span>
                  <span style={{ fontSize: "14px", color: "#000", fontWeight: "500" }}>
                    신청 팀수
                  </span>
                </div>
                <StyledInput
                  placeholder="숫자만 입력해주세요. (예시: 10팀이면 10)"
                  value={teamCountValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setTeamCountValue(e.target.value)
                  }
                  $disabled={false}
                  style={{
                    border: teamCountValue ? "1px solid #007BFF" : "1px solid #d1d5d8",
                  }}
                />
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="flex-1 d-flex fd-c gap-4">
            <div className="d-flex fd-c gap-2">
              <h2 className="bold" style={{ fontSize: "18px", marginBottom: "8px" }}>
                라운드 일자를 선택해주세요.
              </h2>
            </div>

            <div className="d-flex fd-c gap-4">
              {/* 희망 일정 */}
              <div className="d-flex fd-c gap-1">
                <div className="d-flex ai-c gap-1">
                  <span style={{ color: "#FF6B35", fontSize: "14px" }}>*</span>
                  <span style={{ fontSize: "14px", color: "#000", fontWeight: "500" }}>
                    희망 일정
                  </span>
                </div>
                <div className="d-flex gap-2">
                  <button
                    onClick={() => setScheduleType("sameDay")}
                    style={{
                      padding: "12px 16px",
                      border: "1px solid #E0E0E0",
                      borderRadius: "8px",
                      backgroundColor: scheduleType === "sameDay" ? "#007BFF" : "#F8F9FA",
                      color: scheduleType === "sameDay" ? "#FFFFFF" : "#1A1A1A",
                      fontSize: "14px",
                      fontWeight: "400",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                  >
                    당일
                  </button>
                  <button
                    onClick={() => setScheduleType("oneNight")}
                    style={{
                      padding: "12px 16px",
                      border: "1px solid #E0E0E0",
                      borderRadius: "8px",
                      backgroundColor: scheduleType === "oneNight" ? "#007BFF" : "#F8F9FA",
                      color: scheduleType === "oneNight" ? "#FFFFFF" : "#1A1A1A",
                      fontSize: "14px",
                      fontWeight: "400",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                  >
                    1박 2일
                  </button>
                  <button
                    onClick={() => setScheduleType("twoNight")}
                    style={{
                      padding: "12px 16px",
                      border: "1px solid #E0E0E0",
                      borderRadius: "8px",
                      backgroundColor: scheduleType === "twoNight" ? "#007BFF" : "#F8F9FA",
                      color: scheduleType === "twoNight" ? "#FFFFFF" : "#1A1A1A",
                      fontSize: "14px",
                      fontWeight: "400",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                  >
                    2박 3일
                  </button>
                </div>
              </div>

              {/* 일정 시작일 */}
              <div className="d-flex fd-c gap-1">
                <div className="d-flex ai-c gap-1">
                  <span style={{ color: "#FF6B35", fontSize: "14px" }}>*</span>
                  <span style={{ fontSize: "14px", color: "#000", fontWeight: "500" }}>
                    일정 시작일
                  </span>
                </div>
                <div
                  style={{
                    width: "100%",
                    height: "44px",
                    padding: "13px 10px",
                    border: startDate ? "1px solid #007BFF" : "1px solid #d1d5d8",
                    borderRadius: "6px",
                    backgroundColor: "#fdfdfd",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    fontFamily: "Pretendard, -apple-system, Roboto, Helvetica, sans-serif",
                    fontSize: "14px",
                    fontWeight: "400",
                    lineHeight: "18px",
                    color: startDate ? "#1a1a1a" : "#999999",
                    position: "relative",
                  }}
                >
                  <span>
                    {startDate
                      ? new Date(startDate)
                          .toLocaleDateString("ko-KR", {
                            year: "2-digit",
                            month: "2-digit",
                            day: "2-digit",
                          })
                          .replace(/\./g, ".")
                          .replace(/\s/g, "")
                      : "--.--.--"}
                  </span>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      if (new Date(e.target.value) <= new Date()) {
                        alert("미래 날짜를 선택해주세요.");
                        return;
                      }
                      _setStartDate(e.target.value);
                    }}
                    style={{
                      position: "absolute",
                      right: "10px",
                      opacity: 0,
                      cursor: "pointer",
                      width: "30px",
                      height: "30px",
                    }}
                  />
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg
                      width="14"
                      height="16"
                      viewBox="0 0 14 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3.75437 9.5C3.54312 9.5 3.36458 9.42854 3.21875 9.28563C3.07292 9.14271 3 8.96563 3 8.75438C3 8.54313 3.07146 8.36458 3.21438 8.21875C3.35729 8.07292 3.53438 8 3.74563 8C3.95688 8 4.13542 8.07146 4.28125 8.21438C4.42708 8.35729 4.5 8.53438 4.5 8.74563C4.5 8.95688 4.42854 9.13542 4.28562 9.28125C4.14271 9.42708 3.96563 9.5 3.75437 9.5ZM7.00437 9.5C6.79313 9.5 6.61458 9.42854 6.46875 9.28563C6.32292 9.14271 6.25 8.96563 6.25 8.75438C6.25 8.54313 6.32146 8.36458 6.46438 8.21875C6.60729 8.07292 6.78438 8 6.99563 8C7.20688 8 7.38542 8.07146 7.53125 8.21438C7.67708 8.35729 7.75 8.53438 7.75 8.74563C7.75 8.95688 7.67854 9.13542 7.53562 9.28125C7.39271 9.42708 7.21562 9.5 7.00437 9.5ZM10.2544 9.5C10.0431 9.5 9.86458 9.42854 9.71875 9.28563C9.57292 9.14271 9.5 8.96563 9.5 8.75438C9.5 8.54313 9.57146 8.36458 9.71437 8.21875C9.85729 8.07292 10.0344 8 10.2456 8C10.4569 8 10.6354 8.07146 10.7812 8.21438C10.9271 8.35729 11 8.53438 11 8.74563C11 8.95688 10.9285 9.13542 10.7856 9.28125C10.6427 9.42708 10.4656 9.5 10.2544 9.5ZM1.5 16C1.0875 16 0.734375 15.8507 0.440625 15.5521C0.146875 15.2535 0 14.9028 0 14.5V3.5C0 3.09722 0.146875 2.74653 0.440625 2.44792C0.734375 2.14931 1.0875 2 1.5 2H3V0H4.5V2H9.5V0H11V2H12.5C12.9125 2 13.2656 2.14931 13.5594 2.44792C13.8531 2.74653 14 3.09722 14 3.5V14.5C14 14.9028 13.8531 15.2535 13.5594 15.5521C13.2656 15.8507 12.9125 16 12.5 16H1.5ZM1.5 14.5H12.5V7H1.5V14.5ZM1.5 5.5H12.5V3.5H1.5V5.5Z"
                        fill="#1A1A1A"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              {/* 라운드 시작 시간 */}
              <div className="d-flex fd-c gap-1">
                <div className="d-flex ai-c gap-1">
                  <span style={{ color: "#FF6B35", fontSize: "14px" }}>*</span>
                  <span style={{ fontSize: "14px", color: "#000", fontWeight: "500" }}>
                    라운드 시작 시간
                  </span>
                </div>
                <div
                  onClick={() => setIsTimePickerOpen(true)}
                  style={{
                    width: "100%",
                    height: "44px",
                    padding: "13px 10px",
                    border: selectedTime ? "1px solid #007BFF" : "1px solid #d1d5d8",
                    borderRadius: "6px",
                    backgroundColor: "#fdfdfd",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    fontFamily: "Pretendard, -apple-system, Roboto, Helvetica, sans-serif",
                    fontSize: "14px",
                    fontWeight: "400",
                    lineHeight: "18px",
                    color: selectedTime ? "#1a1a1a" : "#999999",
                  }}
                >
                  <span>{selectedTime || "--"}</span>
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg
                      width="17"
                      height="16"
                      viewBox="0 0 18 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.00016 15.9999C8.02794 15.9999 7.11732 15.8152 6.26829 15.4458C5.41912 15.0763 4.68023 14.5772 4.05162 13.9485C3.42287 13.3198 2.92377 12.581 2.55433 11.7318C2.18489 10.8828 2.00016 9.97214 2.00016 8.99992C2.00016 8.0277 2.18489 7.11707 2.55433 6.26804C2.92377 5.41888 3.42287 4.67999 4.05162 4.05138C4.68023 3.42263 5.41912 2.92353 6.26829 2.55408C7.11732 2.18464 8.02794 1.99992 9.00016 1.99992C9.97239 1.99992 10.883 2.18464 11.732 2.55408C12.5812 2.92353 13.3201 3.42263 13.9487 4.05138C14.5775 4.67999 15.0766 5.41888 15.446 6.26804C15.8154 7.11707 16.0002 8.0277 16.0002 8.99992C16.0002 9.97214 15.8154 10.8828 15.446 11.7318C15.0766 12.581 14.5775 13.3198 13.9487 13.9485C13.3201 14.5772 12.5812 15.0763 11.732 15.4458C10.883 15.8152 9.97239 15.9999 9.00016 15.9999ZM11.0835 11.8333L12.146 10.7708L9.75016 8.37492V4.99992H8.25016V8.99992L11.0835 11.8333ZM3.93766 0.395752L5.00016 1.45825L1.4585 4.99992L0.395996 3.93742L3.93766 0.395752ZM14.0627 0.395752L17.6043 3.93742L16.5418 4.99992L13.0002 1.45825L14.0627 0.395752ZM8.99558 14.4999C10.5264 14.4999 11.8266 13.9667 12.896 12.9003C13.9654 11.8339 14.5002 10.5353 14.5002 9.0045C14.5002 7.47367 13.967 6.17353 12.9006 5.10409C11.8342 4.03464 10.5356 3.49992 9.00475 3.49992C7.47391 3.49992 6.17377 4.03311 5.10433 5.0995C4.03489 6.16589 3.50016 7.4645 3.50016 8.99534C3.50016 10.5262 4.03336 11.8263 5.09975 12.8958C6.16614 13.9652 7.46475 14.4999 8.99558 14.4999Z"
                        fill="#1A1A1A"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="flex-1 d-flex fd-c gap-4">
            <div className="d-flex fd-c gap-2">
              <h2 className="bold" style={{ fontSize: "18px", marginBottom: "8px" }}>
                라운드 옵션을 선택해주세요.
              </h2>
            </div>

            <div className="d-flex fd-c gap-4">
              {/* 경기 방식 */}
              <div className="d-flex fd-c gap-1">
                <div className="d-flex ai-c gap-1">
                  <span style={{ color: "#FF6B35", fontSize: "14px" }}>*</span>
                  <span style={{ fontSize: "14px", color: "#000", fontWeight: "500" }}>
                    경기 방식
                  </span>
                </div>
                <PlayTypeBoxGroup />
              </div>

              {/* 티 사용 */}
              <div className="d-flex fd-c gap-1">
                <div className="d-flex ai-c gap-1">
                  <span style={{ color: "#FF6B35", fontSize: "14px" }}>*</span>
                  <span style={{ fontSize: "14px", color: "#000", fontWeight: "500" }}>
                    티 사용
                  </span>
                </div>
                <TeeTypeBoxGroup />
              </div>

              {/* 1인 객단가 */}
              <div className="d-flex fd-c gap-1">
                <div className="d-flex ai-c gap-1">
                  <span style={{ color: "#FF6B35", fontSize: "14px" }}>*</span>
                  <span style={{ fontSize: "14px", color: "#000", fontWeight: "500" }}>
                    1인 객단가
                  </span>
                  <span style={{ fontSize: "12px", color: "#666" }}>(월단체 예약의 기준)</span>
                </div>
                <PricePerPersonBoxGroup />
              </div>

              {/* 요청 사항 */}
              <div className="d-flex fd-c gap-1">
                <div className="d-flex ai-c gap-1">
                  <span style={{ fontSize: "14px", color: "#000", fontWeight: "500" }}>
                    요청 사항
                  </span>
                </div>
                <EtcRequirementsBoxGroup />
              </div>

              {/* 시상 요청 */}
              <div className="d-flex fd-c gap-1">
                <div className="d-flex ai-c gap-1">
                  <span style={{ fontSize: "14px", color: "#000", fontWeight: "500" }}>
                    시상 요청
                  </span>
                </div>
                <AwardsBoxGroup />
              </div>
            </div>
          </div>
        );
      case 5:
        return (
          <div className="flex-1 d-flex fd-c gap-4">
            <div className="d-flex fd-c gap-2">
              <h2 className="bold" style={{ fontSize: "18px", marginBottom: "8px" }}>
                추가 옵션을 선택해주세요.
              </h2>
            </div>

            <div className="d-flex fd-c gap-4">
              {/* 식사 선택 */}
              <div className="d-flex fd-c gap-1">
                <div className="d-flex ai-c gap-1">
                  <span style={{ fontSize: "14px", color: "#000", fontWeight: "500" }}>식사</span>
                </div>
                <MealActiveBoxGroup />
                {mealActiveIds?.[0] === 1 && <MealTypeBoxGroup />}
              </div>

              {/* 룸 이용 */}
              <div className="d-flex fd-c gap-1">
                <div className="d-flex ai-c gap-1">
                  <span style={{ fontSize: "14px", color: "#000", fontWeight: "500" }}>
                    룸 이용
                  </span>
                </div>
                <RoomActiveBoxGroup />
              </div>

              {/* 프로샵 이용 */}
              <div className="d-flex fd-c gap-1">
                <div className="d-flex ai-c gap-1">
                  <span style={{ fontSize: "14px", color: "#000", fontWeight: "500" }}>
                    프로샵 이용
                  </span>
                </div>
                <ShopActiveBoxGroup />
                {shopActiveIds?.[0] === 1 && (
                  <div style={{ marginTop: "8px" }}>
                    <Input
                      label="시상품"
                      hasValue={!!shopItemValue}
                      attr={{ value: shopItemValue, onChange: handleShopItemValueChange }}
                    />
                  </div>
                )}
              </div>

              {/* 결제 */}
              <div className="d-flex fd-c gap-1">
                <div className="d-flex ai-c gap-1">
                  <span style={{ fontSize: "14px", color: "#000", fontWeight: "500" }}>결제</span>
                </div>
                <PayTypeBoxGroup />
              </div>

              {/* 기타사항 */}
              <div className="d-flex fd-c gap-1">
                <Input
                  label="기타사항"
                  boldLabel
                  hasValue={!!additionalCommentValue}
                  attr={{ value: additionalCommentValue, onChange: onAdditionalCommentChange }}
                />
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Container>
        {/* Progress Bar */}
        <ProgressContainer>
          <ProgressBackground />
          <ProgressFill width={`${getProgressWidth()}%`} />
        </ProgressContainer>

        {/* Main Content */}
        <MainContent ref={mainContentRef}>{renderCurrentStep()}</MainContent>

        {/* Navigation Buttons */}
        <ButtonContainer>
          {currentStep > 1 && <PrevButton onClick={goToPreviousStep}>이전</PrevButton>}
          <NextButton
            onClick={currentStep < totalSteps ? goToNextStep : handleSubmit}
            disabled={
              (currentStep === 1 && (!selectedClubs || selectedClubs.length < 2)) ||
              (currentStep === 2 &&
                (!proposerNameValue ||
                  !contactValue ||
                  !eventTypeValue ||
                  !eventNameValue ||
                  !teamCountValue)) ||
              (currentStep === 3 && (!scheduleType || !startDate || !selectedTime)) ||
              (currentStep === 4 &&
                (!activePlayTypeIds ||
                  activePlayTypeIds.length === 0 ||
                  !activeTeeTypeIds ||
                  activeTeeTypeIds.length === 0 ||
                  !activePricePerPersonIds ||
                  activePricePerPersonIds.length === 0))
            }
          >
            {currentStep < totalSteps ? "다음" : "제출"}
          </NextButton>
        </ButtonContainer>
      </Container>

      {/* Event Type Modal */}
      <EventTypeModal
        isOpen={isEventTypeModalOpen}
        onClose={() => setIsEventTypeModalOpen(false)}
        onSelect={(selectedType) => setEventTypeValue(selectedType)}
        selectedValue={eventTypeValue}
      />

      {/* Sign Modal */}
      {isSignModalOpened && (
        <SignModalContent
          setSignBase64={setSignBase64}
          name={user?.realname || "사용자"}
          setModalState={setIsSignModalOpened}
          onConfirm={handleFinalSubmit}
          rejectConfirmCondition={Boolean(!signBase64)}
        />
      )}

      {/* Time Picker Modal */}
      {isTimePickerOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(1, 1, 1, 0.86)",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
            zIndex: 9999,
            animation: "fade-in 0.2s",
          }}
          onClick={() => setIsTimePickerOpen(false)}
        >
          <div
            style={{
              maxWidth: "800px",
              width: "calc(100% - 32px)",
              height: "auto",
              minHeight: "400px",
              display: "flex",
              flexDirection: "column",
              borderRadius: "6px",
              background: "#fdfdfd",
              boxShadow: "0 0 4px 0 rgba(0, 0, 0, 0.25)",
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              padding: 0,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                padding: "24px",
                gap: "24px",
                flex: 1,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <h2
                  style={{
                    alignSelf: "stretch",
                    color: "#1a1a1a",
                    fontFamily: "Pretendard, -apple-system, Roboto, Helvetica, sans-serif",
                    fontSize: "18px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "24px",
                    margin: 0,
                  }}
                >
                  시작 시간
                </h2>
              </div>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                  overflowY: "auto",
                  paddingRight: "8px",
                }}
              >
                {times.map((time) => (
                  <button
                    key={time.id}
                    onClick={() => setSelectedTime(time.name)}
                    style={{
                      display: "flex",
                      minWidth: "120px",
                      flex: 1,
                      height: "40px",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      borderRadius: "6px",
                      background: selectedTime === time.name ? "#f1f3f4" : "#f8f9fa",
                      border:
                        selectedTime === time.name ? "1px solid #007bff" : "1px solid #e5e7eb",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        padding: "8px 10px",
                        alignItems: "center",
                        gap: "8px",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          width: "20px",
                          height: "20px",
                          flexShrink: 0,
                          aspectRatio: "1/1",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "50%",
                          backgroundColor: selectedTime === time.name ? "#007bff" : "#d1d5d8",
                          color: "white",
                        }}
                      >
                        {selectedTime === time.name && (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10 3L4.5 8.5L2 6"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                      <span
                        style={{
                          color: "#1a1a1a",
                          fontFamily: "Pretendard, -apple-system, Roboto, Helvetica, sans-serif",
                          fontSize: "14px",
                          fontStyle: "normal",
                          fontWeight: "400",
                          lineHeight: "18px",
                          marginLeft: "10px",
                        }}
                      >
                        {time.name}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                height: "60px",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "auto",
                width: "100%",
              }}
            >
              <button
                onClick={() => setIsTimePickerOpen(false)}
                style={{
                  display: "flex",
                  flex: 1,
                  height: "60px",
                  padding: "16px 46px",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "10px",
                  borderRadius: "0 0 0 6px",
                  background: "#9ca3af",
                  border: "none",
                  cursor: "pointer",
                  color: "#ffffff",
                  textAlign: "center",
                  fontFamily: "Pretendard, -apple-system, Roboto, Helvetica, sans-serif",
                  fontSize: "18px",
                  fontStyle: "normal",
                  fontWeight: "700",
                  lineHeight: "22px",
                }}
              >
                뒤로
              </button>
              <button
                onClick={() => {
                  if (selectedTime) {
                    const timeObj = times.find((time) => time.name === selectedTime);
                    if (timeObj) {
                      _setRoundStartTime(timeObj.str);
                    }
                  }
                  setIsTimePickerOpen(false);
                }}
                style={{
                  display: "flex",
                  flex: 1,
                  height: "60px",
                  padding: "16px 46px",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "10px",
                  borderRadius: "0 0 6px 0",
                  background: "#004b49",
                  border: "none",
                  cursor: "pointer",
                  color: "#ffffff",
                  textAlign: "center",
                  fontFamily: "Pretendard, -apple-system, Roboto, Helvetica, sans-serif",
                  fontSize: "18px",
                  fontStyle: "normal",
                  fontWeight: "700",
                  lineHeight: "22px",
                }}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {showAlertModal && (
        <AlertModal
          onClose={() => setShowAlertModal(false)}
          title="알림"
          subtitle={alertMessage}
          lines={[]}
          confirmText="확인"
          onConfirm={() => setShowAlertModal(false)}
        />
      )}

      {/* Popup Components */}
      <ClubFindPopupComp
        type={"back"}
        headerMsg={"골프장 검색"}
        content={
          <>
            <ClubFindPopupContents
              searchType="event"
              setAnnouncementShowingClub={setAnnouncementShowingClub}
              closeClubSearchPopup={closeClubSearchPopup}
              openAnnounceAgree={openAnnouncePopup}
              groupLocations={groupLocations}
              setSelectedClubs={setSelectedClubs}
              preference={currentClubPreferenceNumber}
              noAnnounce={true}
            />
          </>
        }
      />
      <AnnouncePopupComp
        headerMsg={`${announcementShowingClub?.name} 모집공고`}
        type={"back"}
        content={
          <>
            <AnnouncementAgreePopupContents
              closeMe={closeAnnounce}
              closeParent={closeClubSearchPopup}
              announcementShowingClub={announcementShowingClub}
              preference={currentClubPreferenceNumber}
              setSelectedClubs={setSelectedClubs}
            />
          </>
        }
      />
      {isSubmitLoading && <SreenCoverLoading />}
    </>
  );
}

const Container = styled.div`
  width: 100%;
  height: calc(100dvh - 55px);
  background: #fdfdfd;
  position: relative;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  margin: 0 auto;
`;

const MainContent = styled.div`
  height: calc(100vh - 120px); /* 하단 버튼 공간(120px) 제외 */
  overflow-y: auto;
  padding-top: 4px; /* Progress bar 공간 확보 */
  padding: 19px 15px 130px 15px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;

  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const ProgressContainer = styled.div`
  width: 100%;
  height: 4px;
  position: absolute;
  left: 0px;
  top: 0;
`;

const ProgressBackground = styled.div`
  width: 100%;
  height: 4px;
  background: #d1d5d8;
  position: absolute;
  left: 0px;
  top: 0px;
`;

const ProgressFill = styled.div<{ width: string }>`
  width: ${(props) => props.width};
  height: 4px;
  background: #007bff;
  position: absolute;
  left: 0px;
  top: 0px;
  z-index: 1;
  transition: width 0.3s ease;
`;

const ButtonContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  padding: 26px 16px;
  background: #fdfdfd;
  z-index: 0;
  display: flex;
  gap: 12px;
  width: 100%;
  max-width: 800px;
  box-sizing: border-box;
  border-top: 1px solid #e0e0e0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
`;

const PrevButton = styled.button`
  flex: 1;
  height: 60px;
  padding: 19px 20px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 10px;
  background: white;
  color: #1a1a1a;
  border: 1px solid #1a1a1a;
  font-family: "Pretendard", -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;

  &:hover {
    background-color: #f5f5f5;
  }

  &:active {
    background-color: #e5e5e5;
  }
`;

const NextButton = styled.button`
  flex: 1;
  height: 60px;
  padding: 19px 20px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 10px;
  background: #004b49;
  color: white;
  border: none;
  font-family: "Pretendard", -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;

  &:hover {
    background-color: #003a38;
  }

  &:active {
    background-color: #002a28;
  }

  &:disabled {
    background-color: #d1d5d8;
    color: #79818a;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;
